'use client';

import { useState, useEffect, useMemo } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface PricePoint {
  t: string; // timestamp
  p: number; // price
  c: number; // 24h change at that time
}

export interface PriceHistoryData {
  symbol: string;
  history: PricePoint[];
  lastUpdated: string;
}

interface UsePriceHistoryOptions {
  enabled?: boolean;
}

interface UsePriceHistoryReturn {
  data: PriceHistoryData | null;
  loading: boolean;
  error: string | null;
  // Computed values for easy chart rendering
  prices: number[];
  timestamps: string[];
  isUp: boolean; // Overall 7-day trend
  changePercent: number; // Overall change from first to last price
}

/**
 * Forward-fill gaps in price history for inactive tokens.
 * When a token has no trades for certain hours, the backend doesn't write data,
 * creating gaps. This fills gaps by carrying forward the last known price.
 *
 * @param history - Raw history array (newest first from Firestore)
 * @returns Filled history with continuous hourly data (newest first)
 */
function fillPriceHistoryGaps(history: PricePoint[]): PricePoint[] {
  if (!history || history.length < 2) {
    return history || [];
  }

  // History is newest-first from Firestore
  // We need to process oldest-to-newest for forward-fill, then reverse back
  const chronological = [...history].reverse();

  // Parse timestamps and find the time range
  const parsedPoints = chronological.map(p => ({
    timestamp: new Date(p.t),
    price: p.p,
    change: p.c,
  }));

  const oldestTime = parsedPoints[0].timestamp;
  const newestTime = parsedPoints[parsedPoints.length - 1].timestamp;

  // Generate expected hourly timestamps
  const filledHistory: PricePoint[] = [];
  let currentTime = new Date(oldestTime);
  // Round down to hour
  currentTime.setMinutes(0, 0, 0);

  let pointIndex = 0;
  let lastKnownPrice = parsedPoints[0].price;
  let lastKnownChange = parsedPoints[0].change;

  while (currentTime <= newestTime) {
    // Check if we have a data point near this hour (within 30 min tolerance)
    while (
      pointIndex < parsedPoints.length &&
      parsedPoints[pointIndex].timestamp.getTime() < currentTime.getTime() - 30 * 60 * 1000
    ) {
      // This point is before our current hour, update last known values
      lastKnownPrice = parsedPoints[pointIndex].price;
      lastKnownChange = parsedPoints[pointIndex].change;
      pointIndex++;
    }

    // Check if current point matches this hour (within tolerance)
    if (
      pointIndex < parsedPoints.length &&
      Math.abs(parsedPoints[pointIndex].timestamp.getTime() - currentTime.getTime()) < 30 * 60 * 1000
    ) {
      // Use actual data point
      lastKnownPrice = parsedPoints[pointIndex].price;
      lastKnownChange = parsedPoints[pointIndex].change;
      filledHistory.push({
        t: currentTime.toISOString(),
        p: lastKnownPrice,
        c: lastKnownChange,
      });
      pointIndex++;
    } else {
      // Gap - forward-fill with last known price
      filledHistory.push({
        t: currentTime.toISOString(),
        p: lastKnownPrice,
        c: lastKnownChange,
      });
    }

    // Move to next hour
    currentTime = new Date(currentTime.getTime() + 60 * 60 * 1000);
  }

  // Limit to 168 points (7 days) and return newest-first
  return filledHistory.slice(-168).reverse();
}

/**
 * Hook to fetch price history for a token (7-day sparkline charts)
 * Data is stored in crypto_price_history collection with hourly snapshots
 * Automatically forward-fills gaps for inactive tokens
 */
export function usePriceHistory(
  symbol: string,
  options: UsePriceHistoryOptions = {}
): UsePriceHistoryReturn {
  const { enabled = true } = options;

  const [data, setData] = useState<PriceHistoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !symbol || !db || typeof window === 'undefined') {
      setLoading(false);
      return;
    }

    const fetchHistory = async () => {
      if (!db) return;

      try {
        setLoading(true);
        setError(null);

        const docRef = doc(db, 'crypto_price_history', symbol.toUpperCase());
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const historyData = docSnap.data() as PriceHistoryData;
          setData(historyData);
        } else {
          setData(null);
        }
      } catch (err: any) {
        console.warn(`Failed to fetch price history for ${symbol}:`, err.message);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [symbol, enabled]);

  // Compute derived values with gap filling
  const { prices, timestamps, isUp, changePercent } = useMemo(() => {
    const history = data?.history || [];

    // Fill gaps in history for inactive tokens
    const filledHistory = fillPriceHistoryGaps(history);

    // Reverse to get chronological order (oldest first for charts)
    const chronological = [...filledHistory].reverse();
    const prices = chronological.map(p => p.p);
    const timestamps = chronological.map(p => p.t);

    // Calculate overall change
    let isUp = false;
    let changePercent = 0;

    if (prices.length >= 2) {
      const firstPrice = prices[0];
      const lastPrice = prices[prices.length - 1];
      if (firstPrice > 0) {
        changePercent = ((lastPrice - firstPrice) / firstPrice) * 100;
        isUp = changePercent >= 0;
      }
    }

    return { prices, timestamps, isUp, changePercent };
  }, [data]);

  return {
    data,
    loading,
    error,
    prices,
    timestamps,
    isUp,
    changePercent,
  };
}

/**
 * Hook to fetch price history for multiple tokens at once
 * Uses parallel fetching and forward-fills gaps for inactive tokens
 */
export function useMultiplePriceHistory(symbols: string[]): Map<string, UsePriceHistoryReturn> {
  const [results, setResults] = useState<Map<string, UsePriceHistoryReturn>>(new Map());

  useEffect(() => {
    if (!symbols.length || !db || typeof window === 'undefined') {
      return;
    }

    const fetchAll = async () => {
      if (!db) return;

      // Store db reference for closure (TypeScript narrowing)
      const firestore = db;
      const newResults = new Map<string, UsePriceHistoryReturn>();

      // Fetch all in parallel for better performance
      const fetchPromises = symbols.map(async (symbol) => {
        try {
          const docRef = doc(firestore, 'crypto_price_history', symbol.toUpperCase());
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const data = docSnap.data() as PriceHistoryData;
            const history = data.history || [];

            // Fill gaps in history for inactive tokens
            const filledHistory = fillPriceHistoryGaps(history);

            const chronological = [...filledHistory].reverse();
            const prices = chronological.map(p => p.p);
            const timestamps = chronological.map(p => p.t);

            let isUp = false;
            let changePercent = 0;

            if (prices.length >= 2) {
              const firstPrice = prices[0];
              const lastPrice = prices[prices.length - 1];
              if (firstPrice > 0) {
                changePercent = ((lastPrice - firstPrice) / firstPrice) * 100;
                isUp = changePercent >= 0;
              }
            }

            return {
              symbol: symbol.toUpperCase(),
              result: {
                data,
                loading: false,
                error: null,
                prices,
                timestamps,
                isUp,
                changePercent,
              } as UsePriceHistoryReturn,
            };
          } else {
            return {
              symbol: symbol.toUpperCase(),
              result: {
                data: null,
                loading: false,
                error: null,
                prices: [],
                timestamps: [],
                isUp: false,
                changePercent: 0,
              } as UsePriceHistoryReturn,
            };
          }
        } catch (err: any) {
          return {
            symbol: symbol.toUpperCase(),
            result: {
              data: null,
              loading: false,
              error: err.message,
              prices: [],
              timestamps: [],
              isUp: false,
              changePercent: 0,
            } as UsePriceHistoryReturn,
          };
        }
      });

      const fetchedResults = await Promise.all(fetchPromises);

      for (const { symbol, result } of fetchedResults) {
        newResults.set(symbol, result);
      }

      setResults(newResults);
    };

    fetchAll();
  }, [symbols.join(',')]);

  return results;
}
